import { CampaignPlanWithExtraData } from 'sonata-static-storage/lib/common/common.types';
import { countLineItems, getCampaignPlans, getInsertionOrders } from 'sonata-static-storage/lib/entities';
import { create } from 'zustand';
import createContext from 'zustand/context';

import { getAdvertisers } from '@/services/advertiser.service';
import { persistCampaignsFilter } from './campaignsFiltersPersistence';

export type SelectedMultiflightEntity = {
  id: string;
  isDriveToStoreEligible?: boolean;
  lineItemIds: string[];
};

export type CampaignsStore = {
  advertiserListFilters: Record<string, any>;
  advertisers?: { _id: string; name: string }[];
  cacheSeed: string;
  campaignListFilters: Record<string, any>;
  campaigns?: { _id: string; mediaAdvertiserUser: string; name: string }[];
  countLineItems: (token: Record<string, any>) => Promise<void>;
  deviatedLineItemsListFilters: Record<string, any>;
  getAdvertisers: (token: Record<string, any>) => Promise<void>;
  getCampaigns: (token: Record<string, any>) => Promise<void>;
  getInsertionOrders: (token: Record<string, any>) => Promise<void>;
  hasAdvertisers: boolean;
  hasCampaigns: boolean;
  hasInsertionOrders: boolean;
  hasLineItems: boolean;
  insertionOrders?: { _id: string; campaignPlan: string; name: string }[];
  ioListFilters: Record<string, any>;
  isLoadingCountLineItems: boolean;
  isLoadingGetAdvertisers: boolean;
  isLoadingGetCampaigns: boolean;
  isLoadingGetInsertionOrders: boolean;
  lineItemsListFilters: Record<string, any>;
  selectedCps: SelectedMultiflightEntity[];
  selectedIos: SelectedMultiflightEntity[];
  selectedLineItems: SelectedMultiflightEntity[];
  setAdvertiserListFilters: (newFilters: Record<string, any>) => void;
  setCacheSeed: () => void;
  setDeviatedLineItemsListFilters: (newFilters: Record<string, any>) => void;
  setHasAdvertisers: (has: boolean) => void;
  setCampaignListFilters: (newFilters: Record<string, any>) => void;
  setHasCampaigns: (has: boolean) => void;
  setHasInsertionOrders: (has: boolean) => void;
  setHasLineItems: (has: boolean) => void;
  setIoListFilters: (newFilters: Record<string, any>) => void;
  setLineItemsListFilters: (newFilters: Record<string, any>) => void;
  setSelectedCps: (cps: SelectedMultiflightEntity[]) => void;
  setSelectedIos: (ios: SelectedMultiflightEntity[]) => void;
  setSelectedLineItems: (lineItems: SelectedMultiflightEntity[]) => void;
  setShowSearchByFilters: (show?: boolean) => void;
  showSearchByFilters: boolean;
};
const { Provider, useStore } = createContext();

const initialState = (filters?: Partial<CampaignsStore>) => ({
  advertiserListFilters: filters?.advertiserListFilters ?? {},
  cacheSeed: Math.random().toString(),
  campaignListFilters: filters?.campaignListFilters ?? {},
  deviatedLineItemsListFilters: filters?.deviatedLineItemsListFilters ?? {},
  hasAdvertisers: false,
  hasCampaigns: false,
  hasInsertionOrders: false,
  hasLineItems: false,
  ioListFilters: filters?.ioListFilters ?? {},
  isLoadingCountLineItems: false,
  isLoadingGetAdvertisers: false,
  isLoadingGetCampaigns: false,
  isLoadingGetInsertionOrders: false,
  lineItemsListFilters: filters?.lineItemsListFilters ?? {},
  selectedCps: [],
  selectedIos: [],
  selectedLineItems: [],
  showSearchByFilters: false
});

const createStore = (filters: Partial<CampaignsStore>, userId: string) =>
  create<CampaignsStore>(
    set =>
      ({
        ...initialState(filters),
        countLineItems: async (token: Record<string, any>) => {
          set({ isLoadingCountLineItems: true });
          const numberOfLineItems = await countLineItems(userId, token);
          set({ hasLineItems: numberOfLineItems > 0, isLoadingCountLineItems: false });
        },
        getAdvertisers: async (token: Record<string, any>) => {
          set({ isLoadingGetAdvertisers: true });
          const { items, totalItems } = await getAdvertisers(
            userId,
            {
              includeExtraData: true
            },
            token
          );
          set({
            advertisers: items.map(({ _id, name }) => ({
              _id: _id!,
              name
            })),
            hasAdvertisers: totalItems > 0,
            isLoadingGetAdvertisers: false
          });
        },
        getCampaigns: async (token: Record<string, any>) => {
          set({ isLoadingGetCampaigns: true });
          const { items, totalItems } = await getCampaignPlans(
            userId,
            {
              fields: ['_id', 'name', 'user', 'mediaAdvertiserUser'],
              includeExtraData: true,
              sortBy: 'updated',
              sortOrder: 'desc',
              unlimited: true
            },
            token
          );
          set({
            campaigns: items.map(({ _id, ...rest }) => ({
              _id: _id!,
              ...rest
            })),
            hasCampaigns: totalItems > 0,
            hasInsertionOrders: (items as CampaignPlanWithExtraData[]).some(
              cp => !!cp.extraData.insertionOrderIds.length
            ),
            isLoadingGetCampaigns: false
          });
        },
        getInsertionOrders: async (token: Record<string, any>) => {
          set({ isLoadingGetInsertionOrders: true });
          const { items } = await getInsertionOrders(
            userId,
            {
              fields: ['_id', 'name', 'user', 'campaignPlan'],
              sortBy: 'updated',
              sortOrder: 'desc'
            },
            token
          );
          set({
            insertionOrders: items
              .map(item => item.entity)
              .map(({ _id, ...rest }) => ({
                _id: _id!,
                ...rest
              })),
            isLoadingGetInsertionOrders: false
          });
        },
        setAdvertiserListFilters: (newFilters: Record<string, any>) => {
          set({ advertiserListFilters: newFilters });
          persistCampaignsFilter(userId, 'advertiserListFilters', newFilters);
        },
        setCacheSeed: () => set({ cacheSeed: new Date().toISOString() }),
        setCampaignListFilters: (newFilters: Record<string, any>) => {
          set({ campaignListFilters: newFilters });
          persistCampaignsFilter(userId, 'campaignListFilters', newFilters);
        },
        setDeviatedLineItemsListFilters: (newFilters: Record<string, any>) => {
          set({ deviatedLineItemsListFilters: newFilters });
          persistCampaignsFilter(userId, 'deviatedLineItemsListFilters', newFilters);
        },
        setHasAdvertisers: (has: boolean) => set({ hasAdvertisers: has }),
        setHasCampaigns: (has: boolean) => set({ hasCampaigns: has }),
        setHasInsertionOrders: (has: boolean) => set({ hasInsertionOrders: has }),
        setHasLineItems: (has: boolean) => set({ hasLineItems: has }),
        setIoListFilters: (newFilters: Record<string, any>) => {
          set({ ioListFilters: newFilters });
          persistCampaignsFilter(userId, 'ioListFilters', newFilters);
        },
        setLineItemsListFilters: (newFilters: Record<string, any>) => {
          set({ lineItemsListFilters: newFilters });
          persistCampaignsFilter(userId, 'lineItemsListFilters', newFilters);
        },
        setSelectedCps: (cps: SelectedMultiflightEntity[]) => set({ selectedCps: cps }),
        setSelectedIos: (ios: SelectedMultiflightEntity[]) => set({ selectedIos: ios }),
        setSelectedLineItems: (lineItems: SelectedMultiflightEntity[]) => set({ selectedLineItems: lineItems }),
        setShowSearchByFilters: (show?: boolean) =>
          set(state => ({ showSearchByFilters: show !== undefined ? show : !state.showSearchByFilters }))
      }) as CampaignsStore
  );

export { getPersistedCampaignsFiltersForUser } from './campaignsFiltersPersistence';
export { createStore, Provider, useStore };
